const CACHE_PREFIX = "cacheUtil_";
const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds

// In-memory cache
const memoryCache = new Map();

// Helper function to get current timestamp
const now = () => new Date().getTime();

// Helper function to stringify cache item
const stringifyItem = (value, mode, absoluteTtl, slidingTtl) =>
  JSON.stringify({
    value,
    mode,
    absoluteTtl: absoluteTtl ? now() + absoluteTtl : null,
    slidingTtl,
    lastAccessed: now(),
  });

// Helper function to parse cache item
const parseItem = (itemStr) => {
  if (!itemStr) return null;
  return JSON.parse(itemStr);
};

const cache = {
  set: (key, value, options = {}) => {
    const { mode = "memory", absoluteTtl, slidingTtl } = options;
    const fullKey = CACHE_PREFIX + key;
    // Cap absoluteTtl to 1 week for localStorage
    const cappedAbsoluteTtl =
      mode === "local" && absoluteTtl
        ? Math.min(absoluteTtl, WEEK_IN_MS)
        : absoluteTtl;
    const item = stringifyItem(value, mode, cappedAbsoluteTtl, slidingTtl);
    switch (mode) {
      case "local":
        localStorage.setItem(fullKey, item);
        break;
      case "session":
        sessionStorage.setItem(fullKey, item);
        break;
      case "memory":
      default:
        memoryCache.set(fullKey, item);
    }
    log("set", key, value, options);
  },

  get: (key) => {
    const fullKey = CACHE_PREFIX + key;
    let itemStr;
    // Try to get from memory first
    itemStr = memoryCache.get(fullKey);
    // If not in memory, check sessionStorage
    if (!itemStr) {
      itemStr = sessionStorage.getItem(fullKey);
    }
    // If not in sessionStorage, check localStorage
    if (!itemStr) {
      itemStr = localStorage.getItem(fullKey);
    }
    if (!itemStr) {
      if (key !== "is_emulating") {
        log("miss", key);
      }
      return null;
    }
    const item = parseItem(itemStr);
    // Check if item has expired
    if (item.absoluteTtl && now() > item.absoluteTtl) {
      cache.remove(key);
      return null;
    }
    // Update sliding expiration
    if (item.slidingTtl) {
      log("updating sliding expiration", key);
      item.lastAccessed = now();
      cache.set(key, item.value, {
        mode: item.mode,
        absoluteTtl: item.absoluteTtl,
        slidingTtl: item.slidingTtl,
      });
    }
    log("hit", key, item.value);
    return item.value;
  },

  remove: (key) => {
    log("explicit cache remove", key);
    const fullKey = CACHE_PREFIX + key;
    memoryCache.delete(fullKey);
    sessionStorage.removeItem(fullKey);
    localStorage.removeItem(fullKey);
  },

  peek: (key) => {
    const fullKey = CACHE_PREFIX + key;
    let itemStr;
    // Try to get from memory first
    itemStr = memoryCache.get(fullKey);
    // If not in memory, check sessionStorage
    if (!itemStr) {
      itemStr = sessionStorage.getItem(fullKey);
    }
    // If not in sessionStorage, check localStorage
    if (!itemStr) {
      itemStr = localStorage.getItem(fullKey);
    }
    if (!itemStr) {
      return null;
    }
    const item = parseItem(itemStr);
    // Check if item has expired
    if (item.absoluteTtl && now() > item.absoluteTtl) {
      return null;
    }
    // Check if item has expired due to sliding TTL
    if (item.slidingTtl && now() > item.lastAccessed + item.slidingTtl) {
      return null;
    }
    return item.value;
  },

  scavenge: () => {
    log("scavenger run");
    const currentTime = now();
    // Check memory cache
    for (const [key, itemStr] of memoryCache.entries()) {
      const item = parseItem(itemStr);
      if (
        (item.absoluteTtl && currentTime > item.absoluteTtl) ||
        (item.slidingTtl && currentTime > item.lastAccessed + item.slidingTtl)
      ) {
        memoryCache.delete(key);
        log("scavenger remove from memory", key);
      }
    }
    // Check sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key.startsWith(CACHE_PREFIX)) {
        if (cache.peek(key.slice(CACHE_PREFIX.length)) === null) {
          sessionStorage.removeItem(key);
          log("scavenger remove from session", key);
        }
      }
    }
    // Check localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(CACHE_PREFIX)) {
        if (cache.peek(key.slice(CACHE_PREFIX.length)) === null) {
          localStorage.removeItem(key);
          log("scavenger remove from local", key);
        }
      }
    }
  },
};

const log = (...args) => {
  // if (window.location.href.includes("localhost")) {
  //   console.log("[cacheUtil]", ...args);
  // }
};

// Run scavenger every minute
setInterval(cache.scavenge, 60000);

export default cache;
